// ============================================================
// Moteur de combat CO-OP — parité totale avec le combat solo.
// Toutes les fonctions MUTENT le brouillon `s` (game_state) passé par
// publish((s) => {...}) qui fait déjà un structuredClone.
//
// On réutilise les fonctions PURES de gameLogic (executeCompetence,
// effectiveStats, mergeStatus, tickStatusEffects, etc.) afin que les
// mécaniques (buffs, soins, statuts, moutons, posture…) soient IDENTIQUES
// à celles du solo.
// ============================================================
import {
  CLASSES, ENERGY_MAX, BERGER_MUTTONS_INIT, BERGER_MUTTONS_MAX,
} from "../data/classes";
import {
  executeCompetence, effectiveStats, mergeStatus, computeBuffsValues,
  tickStatusEffects, newStatusEffects, computeDamageWithFormula,
  goldAtkBonus, goldUltBonus, goldStealAmount, rollStatusResist,
} from "./gameLogic";
import { tickEnemyCooldowns, chooseEnemyAbility } from "./enemyAI";

export const energyCap = (cid) => CLASSES[cid]?.energyMax || ENERGY_MAX;

// Type d'effet visuel d'attaque selon l'élément (mappe vers AttackFX).
export const elementFx = (element) => {
  switch (element) {
    case "feu": return "fire";
    case "eau": return "ice";
    case "terre": return "earth";
    case "air": return "wind";
    case "lumiere": return "light";
    case "dark": return "shadow";
    default: return "slash";
  }
};

let _fxSeq = 0;
const nextFxTs = () => Date.now() + (++_fxSeq % 1000) / 1000;
// Pose un évènement d'animation éphémère dans l'état partagé (rejoué par tous les clients).
const setFx = (c, fx) => { c.fx = { ...fx, ts: nextFxTs() }; };

// Structures d'effets/moutons à attacher au combat à sa création.
export const freshCombatExtras = (players, aliveUids) => {
  const playerEffects = {};
  const muttons = {};
  for (const uid of aliveUids) {
    playerEffects[uid] = newStatusEffects();
    muttons[uid] = CLASSES[players[uid]?.classe_id]?.hasMuttons ? BERGER_MUTTONS_INIT : 0;
  }
  return { enemyEffects: newStatusEffects(), playerEffects, muttons };
};

const applyCombatBuffsPure = (effects, buffValues) => {
  const ne = { ...effects, combatBuffs: { ...(effects.combatBuffs || {}) } };
  for (const [k, v] of Object.entries(buffValues || {})) {
    ne.combatBuffs[k] = (ne.combatBuffs[k] || 0) + v;
  }
  return ne;
};

const pushLog = (c, logs) => { c.log = [...(c.log || []), ...logs].slice(-8); };

// Plume de Phénix : renaissance à 50% PV, sinon mort.
const killOrRevive = (s, uid, logs) => {
  const pl = s.players[uid];
  if ((pl.character.stats.pv || 0) > 0) return;
  if ((pl.character.reviveTokens || 0) > 0) {
    const pvMax = pl.character.stats.pvMax || pl.character.stats.pv || 1;
    pl.character = {
      ...pl.character,
      reviveTokens: (pl.character.reviveTokens || 1) - 1,
      stats: { ...pl.character.stats, pv: Math.max(1, Math.ceil(pvMax * 0.5)) },
    };
    logs.push(`🪶 Plume de Phénix : ${pl.name} renaît à 50% PV !`);
  } else {
    pl.alive = false;
    logs.push(`☠ ${pl.name} est tombé ! Réanime-le avec un sérum.`);
  }
};

// ============================================================
// ACTION JOUEUR : exécute une compétence (tous types, comme le solo)
// ============================================================
export const applyPlayerSkill = (s, uid, comp) => {
  const c = s.combat;
  if (!c || c.phase !== "player" || c.turnOrder[c.turnIndex] !== uid) return;
  const meS = s.players[uid];
  const myClass = CLASSES[meS.classe_id];
  if (!c.playerEffects) c.playerEffects = {};
  if (!c.playerEffects[uid]) c.playerEffects[uid] = newStatusEffects();
  if (!c.muttons) c.muttons = {};
  const pe = c.playerEffects[uid];
  const logs = [`✨ ${comp.nom}`];

  // ----- Moutons consommés -----
  const myMuttons = c.muttons[uid] ?? 0;
  let muttonsConsumed = 0;
  if (comp.consumeAllMuttons) muttonsConsumed = Math.min(myMuttons, 5);
  else if (comp.consumeMuttons) muttonsConsumed = comp.consumeMuttons;
  muttonsConsumed = Math.min(muttonsConsumed, Math.max(0, myMuttons - 1));

  // ----- Stats effectives + extras -----
  const buffs = pe.combatBuffs || {};
  const goldEarned = meS.character.runStats?.goldEarned || 0;
  const baseStats = myClass?.scalesWithGold
    ? { ...meS.character.stats, atk: (meS.character.stats.atk || 0) + goldAtkBonus(goldEarned) }
    : meS.character.stats;
  const playerStats = effectiveStats(baseStats, buffs);
  const enemyStats = effectiveStats(c.enemy.stats, c.enemyEffects?.combatBuffs || {});
  const extras = {
    pvBns: buffs.pv || 0, defBns: buffs.def || 0, atkBns: buffs.atk || 0, vitBns: buffs.vit || 0,
    muttonsConsumed, goldPower: goldUltBonus(goldEarned),
    enemyEffects: c.enemyEffects, atkElement: myClass?.element, defElement: c.enemy.element,
  };
  const result = executeCompetence(comp, playerStats, enemyStats, extras);

  let curEnemyHp = c.enemyHp;
  let damageThisAction = 0;
  let critThisAction = false;
  let executed = false;

  // Exécution (Monde Ténébreux)
  if (result.executeIfVitLower && (c.enemy.stats.vit || 0) <= (extras.vitBns || 0)) {
    executed = true;
    damageThisAction += curEnemyHp;
    curEnemyHp = 0;
    logs.push("💀 EXÉCUTION ! L'ennemi est exécuté instantanément !");
  }

  if (!executed) {
    for (const hit of result.hits) {
      if (hit.hit) {
        curEnemyHp = Math.max(0, curEnemyHp - hit.damage);
        damageThisAction += hit.damage;
        if (hit.crit) critThisAction = true;
        logs.push(`${hit.damage} dégâts${hit.crit ? " (CRITIQUE)" : ""}`);
      } else {
        logs.push("Esquive !");
      }
    }
  }

  // Bâtisseur : vol d'or à chaque attaque qui touche
  if (myClass?.stealsGold && comp.type === "attaque" && damageThisAction > 0) {
    const stolen = goldStealAmount(s.floor || 1, meS.character.runLevel || 1);
    const rs = { ...(meS.character.runStats || {}) };
    rs.goldEarned = (rs.goldEarned || 0) + stolen;
    meS.character = { ...meS.character, gold: (meS.character.gold || 0) + stolen, runStats: rs };
    logs.push(`💰 Or volé : +${stolen} 🪙`);
  }

  // Suivi des dégâts (runStats)
  if (damageThisAction > 0) {
    const rs = { ...(meS.character.runStats || {}), damageBySkill: { ...(meS.character.runStats?.damageBySkill || {}) } };
    rs.totalDamageDealt = (rs.totalDamageDealt || 0) + damageThisAction;
    rs.maxHit = Math.max(rs.maxHit || 0, damageThisAction);
    rs.damageBySkill[comp.nom] = (rs.damageBySkill[comp.nom] || 0) + damageThisAction;
    meS.character = { ...meS.character, runStats: rs };
  }

  // Détonation Embrasée
  if (comp.detonateBurn) {
    if (c.enemyEffects?.brulure && c.enemyEffects.brulure.duration > 0) {
      const b = c.enemyEffects.brulure;
      const remaining = (b.duration || 0) * (b.damage || 0);
      const boom = Math.max(0, Math.round(remaining * comp.detonateBurn));
      if (boom > 0) {
        curEnemyHp = Math.max(0, curEnemyHp - boom);
        damageThisAction += boom;
        critThisAction = true;
        logs.push(`💥 Détonation des brûlures : ${boom} dégâts (${comp.detonateBurn}× ${remaining} restants) !`);
      }
      c.enemyEffects = { ...c.enemyEffects, brulure: null };
    } else {
      logs.push("💨 Aucune brûlure à faire exploser !");
    }
  }

  // BOSS : interruption d'une charge si l'action inflige >= seuil
  if (c.charging && damageThisAction >= c.charging.threshold) {
    logs.push(`💥 Charge INTERROMPUE ! ${c.charging.sigNom} annulé !`);
    c.charging = null;
  }

  // Effets sur l'ENNEMI
  if (result.applyOnEnemy) {
    c.enemyEffects = mergeStatus(c.enemyEffects, result.applyOnEnemy);
    if (result.applyOnEnemy._buffsToApply) {
      const eStats = effectiveStats(c.enemy.stats, c.enemyEffects.combatBuffs || {});
      const bv = computeBuffsValues(result.applyOnEnemy._buffsToApply, eStats, c.enemyEffects.combatBuffs || {});
      c.enemyEffects = applyCombatBuffsPure(c.enemyEffects, bv);
    }
    if (result.applyOnEnemy.brulure) logs.push("🔥 Brûlure appliquée");
    if (result.applyOnEnemy.gel) logs.push(`❄️ Gel appliqué (${result.applyOnEnemy.gel.duration} tour${result.applyOnEnemy.gel.duration > 1 ? "s" : ""})`);
    if (result.applyOnEnemy.stun) logs.push("⭐ Ennemi étourdi");
  } else if (comp.applyOnEnemy && result.enemyStatusResisted) {
    logs.push(`🛡️ L'ennemi résiste à l'effet néfaste (RÉS ${c.enemy.stats.res || 0}%)`);
  } else if (comp.applyOnEnemy && result.enemyStatusLanded === false) {
    logs.push("💨 L'effet néfaste rate (précision)");
  }

  // Effets sur SOI (sauf si compétence d'équipe — gérée via allyBuff plus bas)
  let npe = pe;
  if (result.applyOnSelf && !comp.allyBuff) {
    npe = mergeStatus(npe, result.applyOnSelf);
    if (result.applyOnSelf._buffsToApply) {
      const refStats = effectiveStats(meS.character.stats, npe.combatBuffs || {});
      const curBuffs = npe.combatBuffs || {};
      const detailed = result.applyOnSelf._buffsToApply.map((b) => {
        let val = b.value || 0;
        if (b.fromStat) val = refStats[b.fromStat] || 0;
        if (b.doubleBns) val = curBuffs[b.doubleBns] || 0;
        return { stat: b.stat, value: val, duration: b.duration || 0 };
      });
      const bv = {};
      for (const d of detailed) bv[d.stat] = (bv[d.stat] || 0) + d.value;
      npe = applyCombatBuffsPure(npe, bv);
      const timed = detailed.filter((d) => d.duration > 0).map((d) => ({ stat: d.stat, value: d.value, duration: d.duration }));
      if (timed.length) npe = { ...npe, timedBuffs: [...(npe.timedBuffs || []), ...timed] };
      const parts = detailed.map((d) => `+${d.value} ${d.stat.toUpperCase()}${d.duration ? ` (${d.duration}t)` : ""}`).join(", ");
      if (parts) logs.push(`✨ Buff : ${parts}`);
    }
    if (result.applyOnSelf.posture) logs.push("🦅 Posture armée pour le prochain tour");
    if (result.applyOnSelf.provoque) logs.push("📣 Provocation appliquée");
  }
  c.playerEffects[uid] = npe;

  // Soins
  const healTo = (amt) => {
    const st = { ...meS.character.stats };
    st.pv = Math.min(st.pvMax, st.pv + amt);
    meS.character = { ...meS.character, stats: st };
  };
  if (result.healFlat) { healTo(result.healFlat); logs.push(`💚 +${result.healFlat} PV`); }
  if (result.healFormula) {
    let h = 0;
    for (const t of result.healFormula) h += playerStats[t] || 0;
    healTo(h); logs.push(`💚 +${h} PV`);
  }
  if (result.healPercent) {
    const h = Math.round((meS.character.stats.pvMax || 0) * result.healPercent);
    healTo(h); logs.push(`💚 +${h} PV (${Math.round(result.healPercent * 100)}%)`);
  }
  if (result.healFromEnemyCrit) {
    const ec = Math.max(0, Math.round(c.enemyEffects?.combatBuffs?.crit || 0));
    const h = Math.round((meS.character.stats.pvMax || 0) * ec / 100);
    if (h > 0) { healTo(h); logs.push(`💚 +${h} PV (${ec}% crit ennemi drainé)`); }
    else logs.push("💨 L'ennemi n'a aucun bonus de critique à drainer.");
  }
  if (result.healFromBuff) {
    const amt = Math.max(0, Math.round((c.playerEffects[uid].combatBuffs?.[result.healFromBuff] || 0) * (result.healFromBuffMult || 1)));
    if (amt > 0) { healTo(amt); logs.push(`💚 +${amt} PV (bonus ${result.healFromBuff.toUpperCase()} consommé)`); }
    else logs.push(`💨 Aucun bonus de ${result.healFromBuff.toUpperCase()} à consommer.`);
  }

  // Consommation de bonus
  if (result.consumeBuffs) {
    const cur = c.playerEffects[uid];
    const nb = { ...(cur.combatBuffs || {}) };
    for (const k of result.consumeBuffs) nb[k] = 0;
    const nt = (cur.timedBuffs || []).filter((tb) => !result.consumeBuffs.includes(tb.stat));
    c.playerEffects[uid] = { ...cur, combatBuffs: nb, timedBuffs: nt };
    logs.push(`💥 Bonus ${result.consumeBuffs.join(", ").toUpperCase()} consommés`);
  }

  // Moutons
  if (result.consumeMuttons || result.consumeAllMuttons) {
    const newCount = Math.max(1, myMuttons - muttonsConsumed);
    c.muttons[uid] = newCount;
    logs.push(muttonsConsumed > 0 ? `🐑 -${muttonsConsumed} mouton(s)` : "🐑 Dernier mouton conservé !");
  }
  if (result.addMuttons) {
    const newCount = Math.min(BERGER_MUTTONS_MAX, myMuttons + result.addMuttons);
    logs.push(`🐑 +${newCount - myMuttons} mouton(s)`);
    c.muttons[uid] = newCount;
  }

  // Absorption de VIT au kill (Noir Total)
  if (result.absorbVitOnKill && curEnemyHp <= 0) {
    const v = c.enemy.stats.vit || 0;
    c.playerEffects[uid] = applyCombatBuffsPure(c.playerEffects[uid], { vit: v });
    logs.push(`🌑 Absorbe ${v} VIT !`);
  }

  // Compétence de SOUTIEN d'équipe (Renforcement)
  if (comp.allyBuff) {
    const b = comp.allyBuff;
    c.allyBuffs = [...(c.allyBuffs || []), { stat: b.stat, value: b.value, duration: b.duration }];
    logs.push(`${comp.icon || "✨"} ${meS.name} renforce l'équipe : +${b.value}${b.stat === "res" ? "%" : ""} ${b.stat.toUpperCase()} (${b.duration} tours).`);
  }

  // Animation d'attaque synchronisée (rejouée par tous les clients via l'état partagé)
  const isAttack = comp.type === "attaque";
  setFx(c, {
    actor: uid,
    target: isAttack ? "enemy" : uid,
    type: isAttack ? elementFx(comp.element || myClass?.element) : "buff",
    crit: critThisAction,
    ult: !!comp.ultimate,
    shake: isAttack && damageThisAction > 0,
  });

  // Commit
  c.enemyHp = curEnemyHp;
  c.energy[uid] = Math.max(0, (c.energy[uid] ?? 0) - (comp.cout || 0));
  pushLog(c, logs);

  if (curEnemyHp <= 0) { c.phase = "victory"; return; }
  const next = c.turnIndex + 1;
  if (next >= c.turnOrder.length) c.phase = "enemy"; else c.turnIndex = next;
};

// ============================================================
// TOUR ENNEMI (autorité HÔTE)
// ============================================================
export const resolveEnemyTurn = (s) => {
  const c = s.combat;
  if (!c || c.phase !== "enemy") return;
  const alive = c.turnOrder.filter((uid) => s.players[uid]?.alive && (s.players[uid]?.character?.stats?.pv || 0) > 0);
  if (alive.length === 0) { c.phase = "defeat"; return; }

  c.aiState = tickEnemyCooldowns(c.aiState);
  const logs = [];
  const teamRes = (c.allyBuffs || []).filter((b) => b.stat === "res").reduce((a, b) => a + (b.value || 0), 0);

  // Stats effectives d'un joueur cible (combatBuffs perso + RÉS d'équipe)
  const targetEff = (uid) => {
    const st = s.players[uid].character.stats;
    const buffs = { ...(c.playerEffects?.[uid]?.combatBuffs || {}) };
    buffs.res = (buffs.res || 0) + teamRes;
    return effectiveStats(st, buffs);
  };

  // Ennemi gelé / étourdi → passe son tour
  if (c.enemyEffects?.stun?.duration > 0 || c.enemyEffects?.gel?.duration > 0) {
    const label = c.enemyEffects.gel?.duration > 0 ? "❄️ gelé" : "⭐ étourdi";
    if (c.charging) { logs.push(`❄️ ${c.enemy.nom} perd sa concentration — charge annulée !`); c.charging = null; }
    else logs.push(`${c.enemy.nom} est ${label} et passe son tour.`);
    finishEnemyTurn(s, logs);
    return;
  }

  // Cible : joueur vivant avec la plus faible fraction de PV
  let targetUid = alive[0];
  let lowestFrac = Infinity;
  for (const uid of alive) {
    const st = s.players[uid].character.stats;
    const frac = (st.pv || 0) / Math.max(1, st.pvMax || st.pv || 1);
    if (frac < lowestFrac) { lowestFrac = frac; targetUid = uid; }
  }
  const tgtClass = CLASSES[s.players[targetUid].classe_id];

  const enemyStats = effectiveStats(c.enemy.stats, c.enemyEffects?.combatBuffs || {});

  // Libération d'une charge signature
  if (c.charging) {
    const sig = c.charging;
    const blocked = !!sig.blocked;
    c.charging = null;
    const r = computeDamageWithFormula(enemyStats, targetEff(targetUid), {
      formula: ["atk"], multiplier: sig.multiplier || 2.8, atkElement: c.enemy.element, defElement: tgtClass?.element,
    });
    let dmg = r.hit ? r.damage : 0;
    if (blocked && dmg > 0) dmg = Math.max(1, Math.round(dmg * 0.2));
    logs.push(`🔥 ${c.enemy.nom} libère ${sig.sigNom} !`);
    if (r.hit) {
      const st = { ...s.players[targetUid].character.stats };
      st.pv = Math.max(0, st.pv - dmg);
      s.players[targetUid].character = { ...s.players[targetUid].character, stats: st };
      logs.push(`💥 ${sig.sigNom} sur ${s.players[targetUid].name} : -${dmg} PV${blocked ? " (BLOQUÉ −80%)" : ""}${r.crit ? " (CRIT)" : ""}.`);
      if (!blocked && sig.applyOnPlayer) applyEnemyStatusesOnPlayer(s, targetUid, sig.applyOnPlayer, teamRes, logs);
      killOrRevive(s, targetUid, logs);
    } else {
      logs.push(`${s.players[targetUid].name} esquive la charge !`);
    }
    setFx(c, { actor: "enemy", target: targetUid, type: elementFx(c.enemy.element), crit: true, shake: r.hit });
    finishEnemyTurn(s, logs);
    return;
  }

  // Démarrer une charge signature (boss)
  if (c.enemy.isBoss && c.enemy.signature && (c.aiState.cooldowns.__sig || 0) <= 0 && c.aiState.turnCount >= 2) {
    const sig = c.enemy.signature;
    const threshold = Math.max(1, Math.round((c.enemyMaxHp || c.enemy.stats.pv || 1) * 0.10));
    c.charging = { sigNom: sig.nom, telegraph: sig.telegraph, multiplier: sig.multiplier, applyOnPlayer: sig.applyOnPlayer || null, threshold, blocked: false };
    c.aiState.cooldowns.__sig = sig.cooldown || 4;
    logs.push(`⚠️ ${c.enemy.nom} CHARGE — ${sig.nom} ! Infligez ${threshold} dégâts pour INTERROMPRE, ou DÉFENDEZ.`);
    finishEnemyTurn(s, logs);
    return;
  }

  // IA maligne : choix d'une compétence (debuffs réels désormais supportés → mode solo)
  const ability = chooseEnemyAbility(c.enemy, {
    enemyHpFrac: c.enemyHp / Math.max(1, c.enemyMaxHp || c.enemy.stats.pv || 1),
    playerHpFrac: lowestFrac,
    turnCount: c.aiState.turnCount,
    cooldowns: c.aiState.cooldowns,
    playerEffects: c.playerEffects?.[targetUid] || null,
    mode: "solo",
  });

  // Soin
  if (ability && ability.type === "soin") {
    c.aiState.cooldowns[ability.id] = ability.cooldown || 3;
    const maxHp = c.enemyMaxHp || c.enemy.stats.pv || c.enemyHp;
    const healAmt = Math.round(maxHp * (ability.selfHealPercent || 0.15));
    c.enemyHp = Math.min(maxHp, c.enemyHp + healAmt);
    logs.push(`💚 ${c.enemy.nom} — ${ability.nom} : +${healAmt} PV.`);
    setFx(c, { actor: "enemy", target: "enemy", type: "buff", shake: false });
    finishEnemyTurn(s, logs);
    return;
  }

  // Debuff (pas de dégâts)
  if (ability && ability.type === "debuff") {
    c.aiState.cooldowns[ability.id] = ability.cooldown || 3;
    logs.push(`✨ ${c.enemy.nom} — ${ability.nom}`);
    applyEnemyStatusesOnPlayer(s, targetUid, ability.applyOnPlayer, teamRes, logs);
    setFx(c, { actor: "enemy", target: targetUid, type: "shadow", shake: true });
    finishEnemyTurn(s, logs);
    return;
  }

  // Attaque (compétence ou base)
  const isSkill = !!ability;
  if (isSkill) c.aiState.cooldowns[ability.id] = ability.cooldown || 3;
  const r = computeDamageWithFormula(enemyStats, targetEff(targetUid), {
    formula: ["atk"], multiplier: isSkill ? (ability.multiplier || 1) : 1,
    atkElement: c.enemy.element, defElement: tgtClass?.element,
  });
  const label = isSkill ? ability.nom : "frappe";
  if (r.hit) {
    const st = { ...s.players[targetUid].character.stats };
    st.pv = Math.max(0, st.pv - r.damage);
    s.players[targetUid].character = { ...s.players[targetUid].character, stats: st };
    logs.push(`${c.enemy.nom} — ${label} sur ${s.players[targetUid].name} : -${r.damage} PV${r.crit ? " (CRIT)" : ""}.`);
    if (isSkill && ability.applyOnPlayer) applyEnemyStatusesOnPlayer(s, targetUid, ability.applyOnPlayer, teamRes, logs);
    killOrRevive(s, targetUid, logs);
  } else {
    logs.push(`${s.players[targetUid].name} esquive ${isSkill ? ability.nom : "l'attaque"} !`);
  }
  setFx(c, { actor: "enemy", target: targetUid, type: elementFx(c.enemy.element), crit: r.crit, shake: r.hit });
  finishEnemyTurn(s, logs);
};

// Applique les effets d'une compétence ennemie sur un joueur (jet de RÉS).
const applyEnemyStatusesOnPlayer = (s, uid, cfg, teamRes, logs) => {
  if (!cfg) return;
  const c = s.combat;
  const st = s.players[uid].character.stats;
  const resForRoll = { ...st, res: (st.res || 0) + (c.playerEffects?.[uid]?.combatBuffs?.res || 0) + teamRes };
  if (!rollStatusResist(resForRoll)) {
    logs.push(`🛡️ ${s.players[uid].name} résiste à l'effet néfaste !`);
    return;
  }
  let pe = c.playerEffects[uid] || newStatusEffects();
  const incoming = {};
  if (cfg.brulure && c.enemy.element === "feu") {
    const dmg = cfg.brulure.useElem ? Math.max(1, c.enemy.stats.degatsElem || 1) : (cfg.brulure.damage || 1);
    incoming.brulure = { duration: cfg.brulure.duration, damage: Math.max(1, dmg) };
    logs.push(`🔥 ${s.players[uid].name} est brûlé !`);
  }
  if (cfg.stun) { incoming.stun = { duration: (cfg.stun.duration || 1) + 1 }; logs.push(`⭐ ${s.players[uid].name} est étourdi !`); }
  if (cfg.gel && c.enemy.element === "eau") { incoming.gel = { duration: (cfg.gel.duration || 1) + 1 }; logs.push(`❄️ ${s.players[uid].name} est gelé !`); }
  if (Object.keys(incoming).length) pe = mergeStatus(pe, incoming);
  if (cfg.combatBuff) {
    const arr = Array.isArray(cfg.combatBuff) ? cfg.combatBuff : [cfg.combatBuff];
    const bv = {};
    for (const b of arr) bv[b.stat] = (bv[b.stat] || 0) + (b.value || 0);
    pe = applyCombatBuffsPure(pe, bv);
    const parts = Object.entries(bv).map(([k, v]) => `${v > 0 ? "+" : ""}${v} ${k.toUpperCase()}`).join(", ");
    if (parts) logs.push(`🔻 ${parts}`);
  }
  c.playerEffects[uid] = pe;
};

// Posture du Phoenix : attaque différée déclenchée à la fin du tour ennemi.
const releasePostureCoop = (s, uid, logs) => {
  const c = s.combat;
  const pe = c.playerEffects[uid];
  const post = pe?.posture;
  if (!post) return;
  const playerStats = effectiveStats(s.players[uid].character.stats, pe.combatBuffs || {});
  const enemyStats = effectiveStats(c.enemy.stats, c.enemyEffects?.combatBuffs || {});
  const hit = computeDamageWithFormula(playerStats, enemyStats, {
    formula: post.formula, atkElement: CLASSES[s.players[uid].classe_id]?.element, defElement: c.enemy.element,
  });
  if (hit.hit) {
    c.enemyHp = Math.max(0, c.enemyHp - hit.damage);
    logs.push(`🦅 ${s.players[uid].name} — Posture du Phoenix : ${hit.damage} dégâts${hit.crit ? " (CRIT)" : ""}`);
    if (post.stun) {
      if (Math.random() * 100 >= (c.enemy.stats.res || 0)) {
        c.enemyEffects = mergeStatus(c.enemyEffects, { stun: { duration: post.stun } });
        logs.push("⭐ Ennemi étourdi !");
      } else {
        logs.push(`🛡️ L'ennemi résiste au stun (RÉS ${c.enemy.stats.res || 0}%)`);
      }
    }
  } else {
    logs.push(`🦅 ${s.players[uid].name} — Posture : esquive !`);
  }
  c.playerEffects[uid] = { ...pe, posture: null };
};

// Fin du tour ennemi : postures, DOTs, décréments, régénération d'énergie.
const finishEnemyTurn = (s, logs) => {
  const c = s.combat;

  // Postures différées
  for (const uid of c.turnOrder) {
    const pe = c.playerEffects?.[uid];
    if (pe?.posture?.duration > 0 && s.players[uid]?.alive && (s.players[uid]?.character?.stats?.pv || 0) > 0) {
      releasePostureCoop(s, uid, logs);
    }
  }
  if (c.enemyHp <= 0) { c.phase = "victory"; pushLog(c, logs); return; }

  // Tick effets ENNEMI (brûlure + décréments gel/stun)
  const et = tickStatusEffects(c.enemyEffects || newStatusEffects());
  if (et.damage > 0) c.enemyHp = Math.max(0, c.enemyHp - et.damage);
  if (et.logs.length) logs.push(...et.logs);
  c.enemyEffects = et.updatedEffects;
  if (c.enemyHp <= 0) { c.phase = "victory"; pushLog(c, logs); return; }

  // Tick effets de chaque JOUEUR (brûlure + décréments buffs/timed/stun/gel)
  for (const uid of c.turnOrder) {
    const pe = c.playerEffects?.[uid];
    if (!pe) continue;
    const t = tickStatusEffects(pe);
    if (t.damage > 0 && s.players[uid]?.alive && (s.players[uid]?.character?.stats?.pv || 0) > 0) {
      const st = { ...s.players[uid].character.stats };
      st.pv = Math.max(0, st.pv - t.damage);
      s.players[uid].character = { ...s.players[uid].character, stats: st };
      logs.push(`🔥 ${s.players[uid].name} subit ${t.damage} dégâts de brûlure.`);
      if (st.pv <= 0) killOrRevive(s, uid, logs);
    }
    c.playerEffects[uid] = t.updatedEffects;
  }

  // Décrément des bonus d'équipe (Renforcement)
  if (Array.isArray(c.allyBuffs) && c.allyBuffs.length) {
    c.allyBuffs = c.allyBuffs.map((b) => ({ ...b, duration: (b.duration || 0) - 1 })).filter((b) => b.duration > 0);
  }

  const stillAlive = c.turnOrder.filter((uid) => s.players[uid]?.alive && (s.players[uid]?.character?.stats?.pv || 0) > 0);
  if (stillAlive.length === 0) { c.phase = "defeat"; pushLog(c, logs); return; }
  for (const uid of stillAlive) c.energy[uid] = Math.min(energyCap(s.players[uid]?.classe_id), (c.energy[uid] ?? 0) + 1);
  c.turnOrder = stillAlive;
  c.turnIndex = 0;
  c.phase = "player";
  pushLog(c, logs);
};

// Un joueur dont c'est le tour mais qui est étourdi/gelé passe automatiquement.
// Retourne true s'il a été skip (état muté), false sinon.
export const skipStunnedActivePlayer = (s) => {
  const c = s.combat;
  if (!c || c.phase !== "player") return false;
  const uid = c.turnOrder[c.turnIndex];
  const pe = c.playerEffects?.[uid];
  const stunned = (pe?.stun?.duration || 0) > 0;
  const frozen = (pe?.gel?.duration || 0) > 0;
  if (!stunned && !frozen) return false;
  pushLog(c, [`${stunned ? "⭐" : "❄️"} ${s.players[uid].name} est ${stunned ? "étourdi" : "gelé"} et passe son tour.`]);
  const next = c.turnIndex + 1;
  if (next >= c.turnOrder.length) c.phase = "enemy"; else c.turnIndex = next;
  return true;
};
